import { useCallback, useMemo } from "react";
import { ImageStyles, Block, ErrorHandler } from "@business-finland/wif-ui-lib";
import { PageHeader } from "@business-finland/wif-ui-lib/dist/PageHeader";
import { MasonryGrid } from "@business-finland/wif-ui-lib/dist/Masonry";
import type { IRecommendationsCardProps } from "@business-finland/wif-ui-lib/dist/Recommendations";

import useSavedItems from "../contexts/SaveItemsToStorageContext";
import useImageDataHandler from "../hooks/useImageDataHandler";
import buildGetStaticProps from "../service/static-props-api";
import { ISavedItem } from "../types/saved-items";
import { formatPageLink } from "../util/magnolia";

export const getStaticProps = buildGetStaticProps(
  "Saved Items",
  "/saved-items"
);

export default function SavedItemsPage() {
  const mapToCardProps = useMapToCardPropsCallback();
  const { storedItems, isSaved, toggleItemInLocalStorage } = useSavedItems();

  const cards = useMemo(() => {
    if (!storedItems) return [];

    const handleSave = (item: any) => {
      toggleItemInLocalStorage(item);
    };

    return storedItems
      .map((item) =>
        mapToCardProps({
          ...item,
          isSaved: isSaved(item.id),
          onClick: () => handleSave(item),
        })
      )
      .filter(Boolean);
  }, [isSaved, mapToCardProps, storedItems, toggleItemInLocalStorage]);

  return (
    <>
      <PageHeader
        heading="Your saved items"
        leadText="Add your favourites to this section by clicking the heart icon. Happy searching!"
        backgroundColor="blue"
      />
      {cards.length === 0 ? (
        <ErrorHandler
          title={"It looks a bit empty here"}
          description={
            "It doesn’t look like you’ve picked any favourites yet. Click the heart icon to save things you like to this section."
          }
          videoSrc={"/static/videos/Icon_Save.mp4"}
          fallbackSrc={"/static/images/Icon_Save.gif"}
        />
      ) : (
        <Block>
          <MasonryGrid variants={cards} />
        </Block>
      )}
    </>
  );
}

function useMapToCardPropsCallback() {
  const imageDataHandler = useImageDataHandler();

  const getCardImage: (
    introImage: Wif.Mgnl.Image,
    altText: string
  ) => IRecommendationsCardProps["image"] | null = useCallback(
    (introImage, altText) => {
      if (!introImage) return null;

      const imageData = imageDataHandler(introImage);
      return {
        ...imageData,
        altText,
        styling: ImageStyles.MASONRY_CARD,
        fallback: false,
        credits: introImage?.metadata?.rights,
      };
    },
    [imageDataHandler]
  );

  return useCallback(
    (props: ISavedItem) => {
      const {
        id,
        title,
        description,
        pageLink,
        linkDescription,
        linkLabel,
        image,
        isSaved,
        type,
        altText,
        onClick,
      } = props;

      if (type === "linkPreviewCard") {
        if (image) {
          const imageData = imageDataHandler(image);

          const imageProps = {
            ...imageData,
            altText: altText!,
          };

          return {
            linkPreview: {
              card: {
                id,
                cardTitle: title,
                cardIntroText: description,
                link: pageLink,
                linkLabel: linkLabel!,
                linkDescription: linkDescription!,
                imageProps,
                image,
                isSaved,
                onSave: onClick,
              },
              hasImages: true,
              hasSaveButton: true,
            },
          };
        }

        return {
          linkPreview: {
            card: {
              cardTitle: title,
              cardIntroText: description,
              link: pageLink,
              linkLabel: linkLabel!,
              linkDescription: linkDescription!,
              isSaved,
              onSave: onClick,
            },
            hasImages: false,
            hasSaveButton: true,
          },
        };
      }

      return {
        masonry: {
          id,
          title,
          description,
          showSaveButton: true,
          link: formatPageLink(pageLink),
          image: getCardImage(image as Wif.Mgnl.Image, title),
          isSaved,
          onClick,
        },
      };
    },
    [getCardImage, imageDataHandler]
  );
}
